import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';

import {
  crudGetOne as crudGetOneAction,
  crudGetMatching as crudGetMatchingAction,
  translate as t,
  ReferenceLoadingProgress,
  ReferenceError,
} from 'admin-on-rest';
import { getPossibleReferences } from 'admin-on-rest/lib/reducer/admin/references/possibleValues';

import equal from 'deep-equal';

const referenceSource = (resource, source) => `${resource}@${source}`;
const noFilter = () => true;

export const getDataStatus = ({
  input,
  matchingReferences,
  referenceRecord,
  translate,
}) => {
  const matchingReferencesError =
        matchingReferences && matchingReferences.error
          ? translate(matchingReferences.error, {
            _: matchingReferences.error,
          })
          : null;
  const selectedReferenceError =
        input.value && !referenceRecord
          ? translate('aor.input.references.single_missing', {
            _: 'aor.input.references.single_missing',
          })
          : null;

  return {
    waiting:
            (input.value && selectedReferenceError && !matchingReferences) ||
            (!input.value && !matchingReferences),
    error:
            (input.value &&
                selectedReferenceError &&
                matchingReferencesError) ||
            (!input.value && matchingReferencesError)
              ? input.value ? selectedReferenceError : matchingReferencesError
              : null,
    warning: selectedReferenceError || matchingReferencesError,
    choices: Array.isArray(matchingReferences)
      ? matchingReferences
      : [referenceRecord].filter((choice) => choice),
  };
};

export class ReferenceInput extends Component {
  constructor(props) {
    super(props);
    const { perPage, sort, filter } = props;
    // stored as a property rather than state because we don't want redraw of async updates
    this.params = { pagination: { page: 1, perPage }, sort, filter };
    this.debouncedSetFilter = debounce(this.setFilter.bind(this), 500);
  }

  componentDidMount() {
    this.fetchReferenceAndOptions(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.record.id !== nextProps.record.id) {
      this.fetchReferenceAndOptions(nextProps);
    }
    if (!equal(this.props.filter, nextProps.filter)) {
      this.fetchReferenceAndOptions(nextProps);
    }
  }

    setFilter = (filter) => {
      if (filter !== this.params.filter) {
        this.params.filter = this.props.filterToQuery(filter);
        this.fetchReferenceAndOptions(this.props);
      }
    };

    setPagination = (pagination) => {
      if (pagination !== this.param.pagination) {
        this.param.pagination = pagination;
        this.fetchReferenceAndOptions(this.props);
      }
    };

    setSort = (sort) => {
      if (sort !== this.params.sort) {
        this.params.sort = sort;
        this.fetchReferenceAndOptions(this.props);
      }
    };

    fetchReferenceAndOptions(props) {
      const { input, reference, source, resource, filter: filterFromProps } = props;
      const { pagination, sort, filter } = this.params;

      const id = input.value;
      if (id) {
        this.props.crudGetOne(reference, id, null, false);
      }
      const finalFilter = { ...filter, ...filterFromProps };
      this.props.crudGetMatching(
        reference,
        referenceSource(resource, source),
        pagination,
        sort,
        finalFilter
      );
    }

    render() {
      const {
        input,
        resource,
        label,
        source,
        allowEmpty,
        matchingReferences,
        referenceRecord,
        basePath,
        onChange,
        children,
        meta,
        translate,
      } = this.props;

      if (React.Children.count(children) !== 1) {
        throw new Error('<ReferenceInput> only accepts a single child');
      }

      const translatedLabel = translate(
        label || `resources.${resource}.fields.${source}`
      );
      const dataStatus = getDataStatus({
        input,
        matchingReferences,
        referenceRecord,
        translate,
      });

      if (dataStatus.waiting) {
        return <ReferenceLoadingProgress label={translatedLabel} />;
      }

      if (dataStatus.error) {
        return (
          <ReferenceError
            label={translatedLabel}
            error={dataStatus.error}
          />
        );
      }

      return React.cloneElement(children, {
        allowEmpty,
        input,
        label: translatedLabel,
        resource,
        meta: dataStatus.warning
          ? {
            ...meta,
            error: dataStatus.warning,
            touched: true,
          }
          : meta,
        source,
        choices: dataStatus.choices,
        basePath,
        onChange,
        filter: noFilter, // for AutocompleteInput
        setFilter: this.debouncedSetFilter,
        setPagination: this.setPagination,
        setSort: this.setSort,
        translateChoice: false,
      });
    }
}

ReferenceInput.propTypes = {
  allowEmpty: PropTypes.bool,
  basePath: PropTypes.string,
  children: PropTypes.element.isRequired,
  crudGetMatching: PropTypes.func.isRequired,
  crudGetOne: PropTypes.func.isRequired,
  filter: PropTypes.object,
  filterToQuery: PropTypes.func,
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  matchingReferences: PropTypes.array,
  meta: PropTypes.object,
  onChange: PropTypes.func,
  perPage: PropTypes.number,
  reference: PropTypes.string.isRequired,  // eslint-disable-line
  referenceRecord: PropTypes.object,
  resource: PropTypes.string.isRequired,
  sort: PropTypes.shape({
    field: PropTypes.string,
    order: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  source: PropTypes.string,
  translate: PropTypes.func.isRequired,
};

ReferenceInput.defaultProps = {
  addField: true, // eslint-disable-line
  allowEmpty: false,
  filter: {},
  filterToQuery: (searchText) => ({ q: searchText }),
  matchingReferences: null,
  perPage: 25,
  referenceRecord: null,
  sort: { field: 'id', order: 'DESC' },
};

function mapStateToProps(state, props) {
  const referenceId = props.input.value;
  return {
    matchingReferences: getPossibleReferences(
      state,
      referenceSource(props.resource, props.source),
      props.reference,
      [referenceId]
    ),
    referenceRecord:
            state.admin.resources[props.reference].data[referenceId],
  };
}

const ConnectedReferenceInput = connect(mapStateToProps, {
  crudGetOne: crudGetOneAction,
  crudGetMatching: crudGetMatchingAction,
})(ReferenceInput);

ConnectedReferenceInput.defaultProps = {
  addField: true,
};

export default t(ConnectedReferenceInput);
